import Box from "@mui/material/Box";
import TableSortLabel from "@mui/material/TableSortLabel";
import { useCallback, useMemo } from "react";
import { Column } from "src/api/types";
import { ORDER, ORDER_BY } from "src/constants/searchParams";
import { useBatchFilters } from "src/hooks/useBatchFilters";
import { useFilters } from "src/hooks/useFilters";
import { visuallyHidden } from "@mui/utils";

type Props<T> = {
  column: Extract<Column<T>, { orderable: true }>;
  default_sort?: string;
};

type Order = "asc" | "desc";

function SortableLabel<T>({ column, default_sort = "created_at" }: Props<T>) {
  const [orderBy] = useFilters(ORDER_BY, default_sort);
  const [order] = useFilters(ORDER, "desc");
  const updateFilters = useBatchFilters();

  const orderDirection = useMemo(() => {
    return (
      column.orderable
        ? orderBy === column.orderBy
          ? order
          : "asc"
        : undefined
    ) as Order | undefined;
  }, [column.orderBy, column.orderable, order, orderBy]);

  const newOrderDirection = useMemo(() => {
    if (orderBy != column.orderBy) {
      return "asc";
    }

    return order === "asc" ? "desc" : "asc";
  }, [column.orderBy, order, orderBy]);

  const handleClick = useCallback(() => {
    updateFilters((p) => {
      p.set(ORDER, newOrderDirection);
      p.set(ORDER_BY, column.orderBy);

      return p;
    });
  }, [updateFilters, newOrderDirection, column.orderBy]);

  return (
    <TableSortLabel
      active={orderBy === column.orderBy}
      direction={orderDirection}
      onClick={handleClick}
    >
      {orderBy === column.orderBy ? (
        <Box component="span" sx={visuallyHidden}>
          {order === "desc" ? "sorted descending" : "sorted ascending"}
        </Box>
      ) : null}
      {column.label}
    </TableSortLabel>
  );
}

export default SortableLabel;
